<template>
  <div id="search">
    <w-btn
      class="v-btn--flat"
      icon
      :outlined="false"
      @click="focusOnSearchbar"
    >
      <w-icon
        style="z-index: 9999"
        :color="
          showField
            ? 'var(--v-default-darken2)'
            : 'var(--v-primary_font_color-base)'
        "
      >
        {{ !showField ? '$wGlobalSearch' : '$wSearch' }}
      </w-icon>
    </w-btn>

    <input
      v-if="!expertSearchEnabled"
      ref="searchBar"
      v-model="currentSearchInput"
      :class="showField ? 'visible-search-input' : 'hidden-search-input'"
      :placeholder="placeHolder"
      required
      autocomplete="off"
      :spellcheck="false"
      @keyup.enter="openFirstRow"
      @keyup="searchExpression = currentSearchInput"
      @focus="searchbarHasFocus = true"
      @blur="searchbarHasFocus = false"
    >

    <w-tooltip
      v-if="expertSearchEnabled && showField"
      style="z-index: 9999"
    >
      <template #tooltip-activator="{ on, attrs }">
        <w-icon
          style="z-index: 9999"
          v-bind="attrs"
          :color="syntaxError ? 'red darken-1' : 'green darken-1'"
          v-on="on"
        >
          {{ syntaxError ? '$wClose' : '$wCheck' }}
        </w-icon>
      </template>
      <template #tooltip>
        {{ syntaxErrorMessage }}
      </template>
    </w-tooltip>

    <v-menu
      v-if="expertSearchEnabled"
      v-model="menu"
      offset-y
      :close-on-content-click="false"
      :open-on-focus="true"
      :open-on-click="false"
      :close-on-click="true"
      max-height="40vh"
    >
      <template #activator="{ on }">
        <input
          ref="expertSearchBar"
          v-model="expertSearchInput"
          :class="
            showField ? 'visible-expert-search-input' : 'hidden-search-input'
          "
          :placeholder="placeHolder"
          required
          autocomplete="off"
          :spellcheck="false"
          @click="checkSyntaxOfExpertSearch($event.target.selectionStart)"
          @input="checkSyntaxOfExpertSearch($event.target.selectionStart)"
          @keyup.left="checkSyntaxOfExpertSearch($event.target.selectionStart)"
          @keyup.right="checkSyntaxOfExpertSearch($event.target.selectionStart)"
          v-on="on"
        >
      </template>
      <v-list>
        <v-list-item
          v-for="element of filteredAutocomplete"
          :key="element"
          @click="elementClicked(element)"
        >
          {{ element }}
        </v-list-item>
      </v-list>
    </v-menu>

    <w-switch
      v-if="(showField && !globalSearch && !isMobileDevice)"
      ref="expertSearchSwitch"
      v-model="expertSearchEnabled"
      class='expert-switch'
      :label="$t('ExpertSearch.search')"
      @change="handleExpertSearchSwitch($event)"
    />

    <WorkInformationDialog
      ref="noExpertSearchDialog"
      :message="$t('Search.sentences.noExpertSearch')"
    >
      <template #dialog-actions="{ close }">
        <w-btn @click="close">
          {{ $t('Common.ok') }}
        </w-btn>
      </template>
    </WorkInformationDialog>
  </div>
</template>

<script>
//components
import WorkInformationDialog from '@/components/base_components/dialog/icon-dialogs/WorkInformationDialog';

// services & helpers
import { mapActions, mapGetters, mapState } from 'vuex';
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import TABLE_DATA_KEYS from '@/components/base_components/tables/enum/table-data-keys';
import lazyLoading from '@/mixins/lazy-loading-mixin';
import SEARCH_MODULE from '@/configuration/modules/SEARCH_MODULE';
import { useTimerManager } from '@/composables/timer-manager';

const DEBOUNCE_TIME_IN_MS = 750; // 1000 = 1 Second
const OPERATORS = ['=', '!=', '<', '<=', '>=', '>', 'IN', 'NOT_IN'];
const OPERATOR_DESCRIPTIONS = new Map([
  ['=', '(gleich)'],
  ['!=', '(ungleich)'],
  ['<', '(kleiner)'],
  ['<=', '(kleiner oder gleich)'],
  ['>', '(gößer)'],
  ['>=', '(größer oder gleich)'],
  ['IN', '(enthalten in)'],
  ['NOT_IN', '(nicht enthalten in)'],
]);
const CONJUNCTIONS = ['AND', 'OR', 'UND', 'ODER'];
const CONJUNCTION_MAP = new Map([
  ['AND', 'AND'],
  ['UND', 'AND'],
  ['OR', 'OR'],
  ['ODER', 'OR'],
]);
const termTypes = {
  HEADER: 'header',
  OPERATOR: 'operator',
  VALUE: 'value',
  CONJUNCTION: 'conjunction',
  BRACKET: 'bracket',
  OPENED_BRACKET: 'opened bracket',
};

export default {
  name: 'Searchbar',
  components: { WorkInformationDialog },
  props: {
    showField: Boolean,
    placeHolder: String,
    hasExpertSearch: { type: Boolean, default: true},
  },
  setup() {
    const { registerTimeout } = useTimerManager();
    return {
      registerTimeout,
    };
  },
  created() {
    this.handleSetSearchExpression = debounce(
      this.handleSetSearchExpression,
      DEBOUNCE_TIME_IN_MS
    );
  },
  mixins: [
    lazyLoading(
      (self) => self.getPath,
      (self) => ({ search: self.lazySearchValue, columns: self.lazySearchField }),
      50,
      (self) => self.getData
    ),
  ],
  methods: {
    ...mapActions('tableStore', ['setSearchExpression']),
    handleSetSearchExpression(newSearchExpression) {
      if (this.getTable(this.$route) !== undefined) {
        this._handleSetSearchExpressionForTableView(newSearchExpression);
      } else {
        this._handleSetSearchExpressionForGlobalSearchView(newSearchExpression);
      }
    },
    _handleSetSearchExpressionForTableView(newSearchExpression) {
      return this.setSearchExpression({
        search: newSearchExpression,
        $route: this.$route,
      });
    },
    _handleSetSearchExpressionForGlobalSearchView(newSearchExpression) {
      const query = cloneDeep(this.$route.query);

      if (isEmpty(newSearchExpression)) {
        delete query.search;
      } else {
        query.search = newSearchExpression;
      }

      this.$router.replace({ query: query });
    },
    elementClicked(element) {
      let inputStartOfTerm =
        this.expertSearchInput.slice(0, this.selectionStart).lastIndexOf(' ') +
        1;
      if (this.expressionAtCursor.type === termTypes.VALUE) {
        if (element.includes(' ')) element = '"' + element + '"';
        const lastTerm =
          this.expertSearchTerms[this.expressionAtCursor.position - 1];
        if (lastTerm === 'IN' || lastTerm === 'NOT_IN') element = '(' + element;
        inputStartOfTerm = 0;
        for (const operator of OPERATORS) {
          const lastOperator =
            this.expertSearchInput
              .slice(0, this.selectionStart)
              .lastIndexOf(operator) +
            operator.length +
            1;
          if (lastOperator > inputStartOfTerm) inputStartOfTerm = lastOperator;
        }
      }

      if (this.expressionAtCursor.type === termTypes.HEADER) {
        if (element.includes(' ')) element = '"' + element + '"';
        inputStartOfTerm = 0;
        for (const conjunction of CONJUNCTIONS) {
          let lastConjunction = this.expertSearchInput
            .slice(0, this.selectionStart)
            .lastIndexOf(conjunction);
          if (lastConjunction !== -1) lastConjunction += conjunction.length + 1;
          if (lastConjunction > inputStartOfTerm)
            inputStartOfTerm = lastConjunction;
        }
      }

      if (this.expressionAtCursor.type === termTypes.OPERATOR)
        element = element.split(' ')[0];

      if (this.expertSearchInput[inputStartOfTerm] === '(')
        inputStartOfTerm += 1;
      this.selectionStart += element.length + 2;
      this.$refs.expertSearchBar.focus();
      this.$refs.expertSearchBar.setSelectionRange(
        this.selectionStart,
        this.selectionStart
      );
      if (
        this.expressionAtCursor.position ===
          this.expertSearchTerms.length - 1 ||
        this.expertSearchTerms.length === 0
      )
        element += ' ';
      this.expertSearchInput =
        this.expertSearchInput.slice(0, inputStartOfTerm) +
        this.expertSearchInput
          .slice(inputStartOfTerm)
          .replace(this.expressionAtCursor.value, element);
      this.checkSyntaxOfExpertSearch(this.selectionStart);
    },
    checkSyntaxOfExpertSearch(selectionStart) {
      this.registerTimeout(() => (this.menu = true), 0); // workaround, otherwise menu gets closed with a second click on input
      this.syntaxError = false;
      this.syntaxErrorMessage = this.$t('ExpertSearch.correctSyntax');

      let { terms, cursor } = this.getTermsAndCursorPosition(
        this.expertSearchInput,
        selectionStart
      );
      this.expertSearchTerms = terms;
      this.selectionStart = selectionStart;

      if (terms.length === 0) {
        this.expressionAtCursor = {
          value: '',
          type: termTypes.HEADER,
          position: cursor,
        };
        return;
      }

      let openSearchExpression = false;
      let expectedType = termTypes.HEADER;
      let listMode = false;
      let listSize = 0;
      for (let i = 0; i < terms.length; i++) {
        let term = terms[i];
        switch (expectedType) {
          case termTypes.HEADER:
            if (term === '(') {
              if (cursor === i)
                this.expressionAtCursor = {
                  value: term,
                  type: termTypes.BRACKET,
                  position: cursor,
                };
              break;
            }
            expectedType = termTypes.OPERATOR;
            if (cursor === i)
              this.expressionAtCursor = {
                value: term,
                type: termTypes.HEADER,
                position: cursor,
              };

            if (term.charAt(0) == '"' && term.slice(-1) == '"')
              term = term.slice(1, -1);
            if (
              !this.headerInfo.map((element) => element.text).includes(term) &&
              term !== '*'
            ) {
              // syntaxerror: no header (or opened bracket)
              if (!this.syntaxError) {
                this.syntaxError = true;
                this.syntaxErrorMessage = this.$t(
                  'ExpertSearch.syntaxErrorMessages.missingHeader'
                );
              }
            }
            openSearchExpression = true;
            break;
          case termTypes.OPERATOR:
            if (cursor === i)
              this.expressionAtCursor = {
                value: term,
                type: termTypes.OPERATOR,
                position: cursor,
              };
            expectedType = termTypes.VALUE;
            if (!OPERATORS.includes(term)) {
              // syntaxerror: no operator after header
              if (!this.syntaxError) {
                this.syntaxError = true;
                this.syntaxErrorMessage = this.$t(
                  'ExpertSearch.syntaxErrorMessages.missingOperator'
                );
              }
            }
            if (term === 'IN' || term === 'NOT_IN') {
              expectedType = termTypes.OPENED_BRACKET;
              listMode = true;
            }
            break;
          case termTypes.VALUE:
            if (cursor === i)
              this.expressionAtCursor = {
                value: term,
                type: termTypes.VALUE,
                position: cursor,
                field: terms[i - 2],
              };
            if (listMode) {
              if (term === ')') {
                if (cursor === i)
                  this.expressionAtCursor = {
                    value: term,
                    type: termTypes.BRACKET,
                    position: cursor,
                  };
                expectedType = termTypes.CONJUNCTION;
                listMode = false;
                listSize = 0;
                openSearchExpression = false;
                break;
              }
              if (cursor === i)
                this.expressionAtCursor = {
                  value: term,
                  type: termTypes.VALUE,
                  position: cursor,
                  field: terms[i - 3 - listSize],
                };
              listSize += 1;
              break;
            }
            if (cursor === i)
              this.expressionAtCursor = {
                value: term,
                type: termTypes.VALUE,
                position: cursor,
                field: terms[i - 2],
              };
            expectedType = termTypes.CONJUNCTION;
            openSearchExpression = false;
            break;
          case termTypes.CONJUNCTION:
            if (term === ')') {
              if (cursor === i)
                this.expressionAtCursor = {
                  value: term,
                  type: termTypes.BRACKET,
                  position: cursor,
                };
              break;
            }
            if (cursor === i)
              this.expressionAtCursor = {
                value: term,
                type: termTypes.CONJUNCTION,
                position: cursor,
              };
            expectedType = termTypes.HEADER;
            if (i === terms.length - 1 && term === '') break;
            if (!CONJUNCTIONS.includes(term)) {
              // syntaxerror no conjunction (or closed bracket)
              if (!this.syntaxError) {
                this.syntaxError = true;
                this.syntaxErrorMessage = this.$t(
                  'ExpertSearch.syntaxErrorMessages.missingConjunction'
                );
              }
            }
            openSearchExpression = true;
            break;
          case termTypes.OPENED_BRACKET:
            if (cursor === i)
              this.expressionAtCursor = {
                value: term,
                type: termTypes.BRACKET,
                position: cursor,
              };
            if (term !== '(') {
              if (cursor === i)
                this.expressionAtCursor = {
                  value: term,
                  type: termTypes.VALUE,
                  position: cursor,
                  field: terms[i - 2],
                };
              // syntaxerror: list of values expected. list has to start with '('
              if (!this.syntaxError) {
                this.syntaxError = true;
                this.syntaxErrorMessage = this.$t(
                  'ExpertSearch.syntaxErrorMessages.missingList'
                );
              }
            }
            expectedType = termTypes.VALUE;
        }
      }
      if (openSearchExpression) {
        // syntaxerror: some searchexpression is not completed
        if (!this.syntaxError) {
          this.syntaxError = true;
          this.syntaxErrorMessage = this.$t(
            'ExpertSearch.syntaxErrorMessages.openSearchExpression'
          );
        }
      }
    },
    getTermsAndCursorPosition(string, selectionStart) {
      let terms = [];
      let currentTerm = '';
      let level = 0;
      let levelBegin = [0];
      let quotes = false;
      let currentConjunction = [undefined];
      let lastConjunctionPosition = [-1];
      let regroupActive = [false];
      let pushCurrentTerm = false;
      let cursor = undefined;
      for (let i = 0; i < string.length; i++) {
        if (currentTerm === '(' || currentTerm === ')') {
          terms.push(currentTerm);
          currentTerm = '';
        }

        if (i === selectionStart) cursor = terms.length;

        if (string[i] === '"') quotes = !quotes;

        if (string[i] === '(' && !quotes) {
          if (currentTerm !== '') {
            terms.push(currentTerm);
            currentTerm = '';
          }
          level = level + 1;
          levelBegin.push(terms.length);
          currentConjunction.push(undefined);
          lastConjunctionPosition.push(terms.length);
          regroupActive.push(false);
        }

        if (string[i] === ')' && !quotes) {
          if (currentTerm !== '') {
            terms.push(currentTerm);
            currentTerm = '';
          }
          levelBegin.pop();
          currentConjunction.pop();
          lastConjunctionPosition.pop();
          regroupActive.pop();
          level = level - 1;
        }

        if ((string[i] === ' ' || i === string.length - 1) && !quotes) {
          if (i === string.length - 1 && string[i] !== ' ')
            currentTerm += string[i];

          if (currentTerm !== '') pushCurrentTerm = true;

          if (CONJUNCTIONS.includes(currentTerm)) {
            if (currentConjunction[level] === undefined) {
              currentConjunction[level] = CONJUNCTION_MAP.get(currentTerm);
              lastConjunctionPosition[level] = terms.length;
            } else if (
              CONJUNCTION_MAP.get(currentTerm) !== currentConjunction[level]
            ) {
              // conjunction switch at the same level --> regrouping of terms

              if (CONJUNCTION_MAP.get(currentTerm) === 'AND') {
                // switch from OR to AND
                // move terms since last Conjunction and following terms until next Conjunction Switch one level up
                terms.splice(lastConjunctionPosition[level] + 1, 0, '(');
                if (cursor >= lastConjunctionPosition[level] + 1) cursor += 1;
                level += 1;
                levelBegin.push(lastConjunctionPosition[level] + 1);
                currentConjunction.push(CONJUNCTION_MAP.get(currentTerm));
                lastConjunctionPosition.push(terms.length);
                regroupActive.push(true);
              } else {
                // switch from AND to OR
                if (regroupActive[level]) {
                  terms.push(')');
                  if (cursor === terms.length - 1) cursor += 1;
                  levelBegin.pop();

                  currentConjunction.pop();
                  lastConjunctionPosition.pop();
                  regroupActive.pop();
                  level -= 1;
                  lastConjunctionPosition[level] = terms.length;
                } else {
                  // switch from AND to OR without an active Regroup --> All conjunctions of current level until now were AND
                  currentConjunction[level] = CONJUNCTION_MAP.get(currentTerm);
                  terms.splice(levelBegin[level], 0, '(');
                  terms.push(')');
                  if (cursor >= levelBegin[level]) {
                    cursor += 1;
                    if (cursor === terms.length - 1) cursor += 1;
                  }
                  lastConjunctionPosition[level] = terms.length;
                }
              }
            } else {
              lastConjunctionPosition[level] = terms.length;
            }
          }
        } else {
          currentTerm += string[i];
        }

        if (pushCurrentTerm) {
          terms.push(currentTerm);
          currentTerm = '';
          pushCurrentTerm = false;
        }
      }

      if (level > 0) {
        // Syntaxerror: more opened than closed brackets
        if (!this.syntaxError) {
          this.syntaxError = true;
          this.syntaxErrorMessage = this.$t(
            'ExpertSearch.syntaxErrorMessages.moreOpenedBrackets'
          );
        }
      } else if (level < 0) {
        // Syntaxerror: more closed than opened brackets
        if (!this.syntaxError) {
          this.syntaxError = true;
          this.syntaxErrorMessage = this.$t(
            'ExpertSearch.syntaxErrorMessages.moreClosedBrackets'
          );
        }
      }

      if (string.slice(-1) === ' ' || string.slice(-1) === '(') terms.push('');

      if (cursor === undefined) {
        if (terms.length === 0) cursor = 0;
        else cursor = terms.length - 1;
      }

      if (regroupActive[level]) terms.push(')');

      return { terms: terms, cursor: cursor };
    },
    updateLazyItems(searchValue, searchField) {
      this.lazySearchValue = searchValue;
      this.lazySearchField = searchField;
    },
    getHeaderValue(text) {
      return this.headerInfo.find((element) => element.text === text)?.value;
    },
    getData(response) {
      if (this.lazySearchField !== '') {
        const key = this.lazySearchField.replace('data.', '');
        return response?.data?.data
          ?.map((item) => this.getValuesOfDictionary(item.data[key]))
          .flat()
          .map((element) => element.toString())
          .filter((item, index, self) => self.indexOf(item) === index);
      } else {
        const keys = this.headerInfo.map((header) =>
          header.value.replace('data.', '')
        );
        const headerData = response?.data?.data?.map((item) =>
          Object.keys(item.data)
            .filter((key) => keys.includes(key))
            .reduce((obj, key) => {
              obj[key] = item.data[key];
              return obj;
            }, {})
        );
        return headerData
          .map((item) =>
            this.getValuesOfDictionary(item).map((element) =>
              element.toString()
            )
          )
          .flat()
          .filter((element, index, self) => self.indexOf(element) === index)
          .filter((element) =>
            element.toLowerCase().startsWith(this.lazySearchValue.toLowerCase())
          );
      }
    },
    getValuesOfDictionary(obj) {
      // recursively returns Array of all Values if obj is a dictionary
      if (typeof obj === 'object' && !Array.isArray(obj)) {
        return Object.values(obj)
          .map((value) => this.getValuesOfDictionary(value))
          .flat();
      }
      return obj;
    },
    /*******************************************
     * COMPONENTS METHODS
     *******************************************/
    focusOnSearchbar() {
      if (this.expertSearchEnabled)
        this.$refs.expertSearchBar.focus()
      else 
        this.$refs.searchBar.focus();
    },
    looseFocusOnSearchbar() {
      if (this.expertSearchEnabled)
        this.$refs.expertSearchBar.blur()
      else
        this.$refs.searchBar.blur();
    },
    handleExpertSearchSwitch(value) {
      if (value && !this.hasExpertSearch){
        this.$refs.expertSearchSwitch.$children[0].onClick() // reset switch
        this.$refs.noExpertSearchDialog.open()
      }
    },
    /*******************************************
     * SHORTCUTS METHODS
     *******************************************/
    openFirstRow() {
      this.looseFocusOnSearchbar();
      this.$root.$emit('openFirstRow');
    },
  },
  data() {
    return {
      // Notice: Will not change in Vue-Debug-Tool until rerender of component
      // Nevertheless in searchBarShortcutHandler() the value is the correct and current one
      searchbarHasFocus: false,
      expertSearchEnabled: false,
      expertSearchInput: '',
      expressionAtCursor: { value: '', type: 0, position: 0, field: '' },
      selectionStart: 0,
      expertSearchTerms: [],
      lazySearchValue: '',
      lazySearchField: '',
      menu: false,
      syntaxError: false,
      syntaxErrorMessage: this.$t('ExpertSearch.correctSyntax'),
      SEARCH_MODULE: SEARCH_MODULE,
    };
  },
  computed: {
    ...mapState(['activeModule']),
    ...mapGetters('tableStore', ['getTable']),
    currentSearchInput: {
      get() {
        const table = this.getTable(this.$route);
        if (table !== undefined) {
          return table[TABLE_DATA_KEYS.SEARCH_EXPRESSION];
        }
        return this.$route.query.search;
      },
      set(newSearchExpression) {
        this.handleSetSearchExpression(newSearchExpression);
      },
    },
    headerInfo() {
      const table = this.getTable(this.$route);
      if (table !== undefined) {
        return table[TABLE_DATA_KEYS.HEADERS];
      }
      return [];
    },
    filteredAutocomplete() {
      if (this.expressionAtCursor.type == termTypes.HEADER) {
        const header = this.expressionAtCursor.value.replaceAll('"', '');
        return this.headerInfo
          .filter((element) =>
            element.text.toLowerCase().startsWith(header.toLowerCase())
          )
          .map((element) => element.text);
      } else if (this.expressionAtCursor.type == termTypes.OPERATOR) {
        return OPERATORS.filter((element) =>
          element
            .toLowerCase()
            .startsWith(this.expressionAtCursor.value.toLowerCase())
        ).map((element) => element + ' ' + OPERATOR_DESCRIPTIONS.get(element));
      } else if (this.expressionAtCursor.type == termTypes.VALUE) {
        let headerValue = this.expressionAtCursor.field;
        if (headerValue.indexOf(' ') >= 0)
          headerValue = headerValue.slice(1, -1);
        const searchField =
          headerValue === '*' ? '' : this.getHeaderValue(headerValue);
        if (searchField === undefined) return [];
        this.updateLazyItems(
          this.expressionAtCursor.value.replaceAll('"', ''),
          searchField
        );
        return this.lazyItems;
      } else if (this.expressionAtCursor.type === termTypes.CONJUNCTION) {
        return [...new Set(CONJUNCTION_MAP.values())].filter((element) =>
          element
            .toLowerCase()
            .startsWith(this.expressionAtCursor.value.toLowerCase())
        );
      } else return [];
    },
    globalSearch() {
      return (
        this.$route.path === SEARCH_MODULE.CHILDREN.SEARCH_MODULE.routerLink
      );
    },
    isMobileDevice() {
      return this.$vuetify.breakpoint.name === 'xs';
    },
    getPath() {
      return this.$route.path.charAt(0) === "/" ? this.$route.path.substring(1) : this.$route.path
    },
  },
  watch: {
    activeModule(newModule) {
      if (newModule.isSearchable) {
        this.focusOnSearchbar();
      } else {
        this.looseFocusOnSearchbar();
      }
    },
    expertSearchEnabled(newVal) {
      if (newVal) {
        this.currentSearchInput = '';
      } else {
        this.setSearchExpression({
          search: '',
          $route: this.$route,
        });
      }
    },
    globalSearch(newVal) {
      if (newVal) this.expertSearchEnabled = false;
    },
    ['activeModule.furtherInformations.routerLink']() {
      this.expertSearchInput = '';
    },
    expertSearchTerms(newTerms) {
      if (!this.syntaxError) {
        let expertExpression = '';
        let nextTermtype = termTypes.HEADER;
        let bracketOpen = false;
        let listMode = false;
        let listStart = false;
        for (let term of newTerms) {
          if (term === '(') {
            expertExpression += '(';
            if (listMode) listStart = true;
            continue;
          }
          if (term === ')') {
            expertExpression += ')';
            if (listMode) {
              if (bracketOpen) {
                expertExpression += ')';
                bracketOpen = false;
              }
              listMode = false;
              nextTermtype = termTypes.CONJUNCTION;
            }
            expertExpression += ' ';
            continue;
          }

          switch (nextTermtype) {
            case termTypes.HEADER:
              if (term.indexOf(' ') >= 0) term = term.slice(1, -1);
              if (term !== '*') expertExpression += term + ':';
              nextTermtype = termTypes.OPERATOR;
              break;
            case termTypes.OPERATOR:
              if (term === '!=') {
                bracketOpen = true;
                expertExpression += '(NOT ';
              } else if (term === 'IN' || term === 'NOT_IN') {
                listMode = true;
                if (term === 'NOT_IN') {
                  expertExpression += '(NOT ';
                  bracketOpen = true;
                }
              } else if (term !== '=') {
                expertExpression += term;
              }
              nextTermtype = termTypes.VALUE;
              break;
            case termTypes.VALUE:
              nextTermtype = termTypes.CONJUNCTION;

              if (listMode) {
                if (listStart) listStart = false;
                else {
                  expertExpression += 'OR ';
                }
                if (term.slice(-1) === ',') term = term.slice(0, -1);
                nextTermtype = termTypes.VALUE;
              }

              term = term.replaceAll(':', '\\:');
              //check for complex Snytax
              if (
                term
                  .slice(1, -1)
                  .match(/(\s|^)[+-]|((\sAND\s)|(\sOR\s))|[()?']/)
              ) {
                if (!bracketOpen && !listMode) {
                  expertExpression += '(';
                }
                expertExpression += term
                  .replaceAll('"', '')
                  .replaceAll("'", '"');
                if (!listMode) {
                  expertExpression += ')';
                  bracketOpen = false;
                }
                expertExpression += ' ';
              } else {
                if (term.includes(' ')) expertExpression += term;
                else expertExpression += term.replaceAll('"', '');
                if (bracketOpen && !listMode) {
                  expertExpression += ')';
                  bracketOpen = false;
                }
                expertExpression += ' ';
              }
              break;
            case termTypes.CONJUNCTION:
              expertExpression += CONJUNCTION_MAP.get(term) + ' ';
              nextTermtype = termTypes.HEADER;
              break;
            default:
              break;
          }
        }
        this.setSearchExpression({
          search: expertExpression,
          $route: this.$route,
        });
      }
    },
  },
};
</script>

<style lang="scss">
#search {
  position: relative;
  display: flex;
  align-items: center;

  //.search-icon {
  //  position: absolute;
  //  top: 0;
  //  bottom: 0;
  //  left: 15px;
  //
  //  margin-top: auto;
  //  margin-bottom: auto;
  //
  //  z-index: 9999;
  //}
}

input {
  outline: none;
}
input[type='search'] {
  -webkit-appearance: textfield;
  -webkit-box-sizing: content-box;
  font-family: inherit;
  font-size: 100%;
}

input::-webkit-search-decoration,
input::-webkit-search-cancel-button {
  display: none;
}

.search-input {
  padding: 0.563rem 0.625rem 0.563rem 0.625rem;
  width: 2.5rem;
  margin-left: -2.9rem;

  -webkit-border-radius: 10em;
  -moz-border-radius: 10em;
  border-radius: 10em;

  -webkit-transition: all 0.5s;
  -moz-transition: all 0.5s;
  transition: all 0.5s;

  color: $searchbar-color;
  cursor: pointer;
}

.hidden-search-input {
  width: 0;
  @extend .search-input;
}

.visible-search-input {
  @extend .search-input;
  @extend .focus;
}

.visible-expert-search-input {
  @extend .search-input;
  @extend .expert-focus;
}

.expert-switch {
  margin-left: 1rem;
  .v-label {
    @import '../../../styles/variables/_colors.scss';
    color: $h1-color !important;
  }
}

.focus {
  background-color: var(--v-background-base);
  width: 28.125rem;
  padding-left: 2.5rem;
  color: $searchbar-color;
  cursor: auto;
  border: 1px solid $search-bar-border-color;
}

.expert-focus {
  background-color: var(--v-background-base);
  width: 28.125rem;
  padding-left: 5rem;
  color: $searchbar-color;
  cursor: auto;
  border: 1px solid $search-bar-border-color;
}

@media #{map-get($display-breakpoints, 'md-and-up')} {
  #search .focus {
    width: 25vw;
  }
  #search .expert-focus {
    width: 25vw;
  }
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  #search .focus {
    width: 30vw;
  }
  #search .expert-focus {
    width: 30vw;
    margin-left: -7vh;
  }
}
</style>
